/**
 * Auth Context
 *
 * Provides authentication state throughout the app.
 * Uses Supabase auth with session persistence.
 */

import { api } from '@/api/client';
import { rememberAuthMethod } from '@/lib/auth-last-used';
import { clearCachedAvatar } from '@/lib/avatar-cache';
import { PERSIST_KEY } from '@/lib/entitlement-cache-bust';
import { supabase } from '@/lib/supabase';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { del as idbDel } from 'idb-keyval';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    metadata?: { full_name?: string; signup_ref?: string; signup_params?: Record<string, string> },
  ) => Promise<{ user: User | null; session: Session | null }>;
  signOut: () => Promise<void>;
  signInWithOAuth: (
    provider: 'google' | 'github' | 'azure' | 'facebook',
    options?: { redirectTo?: string },
  ) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /** Re-send the signup confirmation email (Supabase `auth.resend`, type 'signup'). */
  resendSignupConfirmation: (email: string) => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // undefined = not yet initialized; null = known signed-out.
  const authUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // Any query fetched under a different auth identity is stale — most
    // critically the login page caches `currentUser: null` (5-min staleTime),
    // and login navigates via SPA (no reload), which left every user-gated
    // query disabled ("No chats found") until a manual refresh. Fires on
    // null→user (login), user→null (logout), and userA→userB; NOT on token
    // refreshes or the re-emitted SIGNED_IN some supabase-js versions send on
    // tab focus, since the id is unchanged there.
    const onAuthIdentityChange = (session: Session | null) => {
      const newId = session?.user?.id ?? null;
      const prevId = authUserIdRef.current;
      if (prevId !== undefined && prevId !== newId) {
        if (prevId !== null) {
          // Leaving an identity (logout, or userA→userB). `invalidateQueries` only
          // marks entries stale and refetches the ACTIVE ones — the cached data
          // survives, so the next user can paint the previous user's rows before
          // their refetch lands. Several caches are keyed without a user id (e.g.
          // ['drip-locks', levels], whose lock map is derived from that user's
          // lesson_progress), so a stale hit is a cross-user read. Drop the data
          // outright, and the persisted IndexedDB snapshot with it — otherwise
          // PersistQueryClientProvider rehydrates the previous user on next load.
          queryClient.clear();
          void idbDel(PERSIST_KEY).catch(() => {});
        } else {
          // Signing IN from a signed-out cache: nothing user-scoped to drop, but the
          // login page cached `currentUser: null` (5-min staleTime) and login is an
          // SPA navigation, so every user-gated query needs a refetch.
          queryClient.invalidateQueries();
        }
      }
      authUserIdRef.current = newId;
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      onAuthIdentityChange(session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      onAuthIdentityChange(session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_IN' && session?.user) {
        // Durable "last used" hint for the login page — Supabase deletes its
        // own localStorage session on sign-out / failed refresh, so this
        // marker is what the next visit's badge usually reads.
        rememberAuthMethod(session);
        api.logActivity({
          type: 'navigation',
          action: 'signed_in',
          entity_type: 'page',
          entity_id: session.user.id,
          entity_name: session.user.email ?? 'unknown',
          description: 'User signed in',
          metadata: {},
        }).catch(() => {});
      } else if (event === 'SIGNED_OUT') {
        api.logActivity({
          type: 'navigation',
          action: 'signed_out',
          entity_type: 'page',
          entity_id: 'session',
          entity_name: 'session',
          description: 'User signed out',
          metadata: {},
        }).catch(() => {});
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error) return;

    // If this is a fresh allowed_signups member trying to sign in for the
    // first time, they don't have an auth.users row yet — Supabase
    // returns "invalid credentials". Bridge the gap: ask the
    // first-time-signup edge function to create the account from the
    // allowlist + this password, then retry sign-in.
    //
    // OAuth users never hit this path (their auth.users row is created on
    // first provider sign-in by Supabase itself). This brings parity to
    // email+password users.
    const isInvalidCredentials =
      error.status === 400 ||
      /invalid login credentials/i.test(error.message ?? '') ||
      (error as { code?: string }).code === 'invalid_credentials';

    if (!isInvalidCredentials) throw error;

    try {
      const { data, error: fnError } = await supabase.functions.invoke<{
        eligible: boolean;
        reason?: string;
      }>('first-time-signup', { body: { email, password } });

      if (fnError) throw error; // surface ORIGINAL invalid-credentials, not the function error
      if (!data?.eligible) throw error; // not on allowlist OR account already exists — show generic error

      // Account just created — retry sign-in. The instant Stripe back-link (linking a
      // customer who already paid on the previous site to their real subscription) now
      // fires server-side for EVERY signup path via the auth.users trigger
      // (hermes migration 478 → stripe-backlink-user), so no client call is needed
      // here — that trigger fires at account creation, before this retry even returns.
      const retry = await supabase.auth.signInWithPassword({ email, password });
      if (retry.error) throw retry.error;
    } catch (bridgeErr) {
      throw bridgeErr instanceof Error ? bridgeErr : error;
    }
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata?: { full_name?: string; signup_ref?: string; signup_params?: Record<string, string> },
    ) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      if (error) throw error;
      return data;
    },
    [],
  );

  const signOut = useCallback(async () => {
    // Invalidate the cached avatar on logout so the next login never inherits
    // the previous user's avatar (cache is keyed per user; this also covers
    // logging back in as the same user with a since-changed avatar).
    const previousUserId = user?.id;
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('AuthContext: signOut error', error);
    } finally {
      if (previousUserId) void clearCachedAvatar(previousUserId);
      setUser(null);
      setSession(null);
    }
  }, [user?.id]);

  const signInWithOAuth = useCallback(
    async (provider: 'google' | 'github' | 'azure' | 'facebook', options?: { redirectTo?: string }) => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: options?.redirectTo ?? `${window.location.origin}/`,
        },
      });
      if (error) throw error;
    },
    [],
  );

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }, []);

  const resendSignupConfirmation = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    if (error) throw error;
  }, []);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      isAuthenticated: !!user,
      signIn,
      signUp,
      signOut,
      signInWithOAuth,
      resetPassword,
      resendSignupConfirmation,
    }),
    [
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      signInWithOAuth,
      resetPassword,
      resendSignupConfirmation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
